import React, { useEffect, useState } from 'react';
import Tabs, { Props as CustomProps } from '../CustomTabs/CustomTabs';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Tab from '@material-ui/core/Tab/Tab';
import makeStyles from '@material-ui/core/styles/makeStyles';
import { Theme } from '@material-ui/core/styles';
import createStyles from '@material-ui/core/styles/createStyles';
import Tooltip from 'components/Tooltip/Tooltip';

export const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      '& .MuiTab-root': {
        '@media (max-width:400px)': {
          padding: 0
        }
      }
    },
    iconTab: {
      '& > span': { flexDirection: 'row-reverse' },
      '&.MuiTab-labelIcon': {
        minHeight: 'auto',
        paddingTop: 0,
      },
      '&.MuiTab-labelIcon > span.MuiTab-wrapper > svg:first-child': {
        margin: theme.spacing(0, 0, 0, 2),
        [theme.breakpoints.down('xs')]: {
          marginLeft: theme.spacing(1),
          width: '0.8em', height: '0.8em'
        },
        '@media (max-width:500px)': {
          marginLeft: 0
        }
      },
    },
  }),
);

export interface TabData {
  translation: string;
  url: string;
  icon?: React.ReactElement;
  tooltip?: string;
  fullWidth?: boolean;
}

export interface P {
  onChange: (value: number) => void;
  tabs: TabData[];
}

export type LocationTabsProps = P & Omit<CustomProps, 'onChange'>;

/**
 * ReportTabs which controls & is controlled by url
 * @param props
 * @constructor
 */
export const LocationTabs: React.FC<LocationTabsProps> = ({ onChange, value, tabs, children, ...restProps }) => {
  const history = useHistory();
  const { t } = useTranslation();
  const classes = useStyles();
  const [changed, setChanged] = useState(false);

  // initial set of tab based on url
  useEffect(() => {
    if (!changed) {
      const { pathname } = history.location;
      const tabIndex = tabs.findIndex((tab) => tab.url === pathname);

      if (tabIndex !== -1) {
        onChange(tabIndex);
      } else {
        window?.history?.replaceState(null, t(tabs[0].translation), tabs[0].url);
      }
      setChanged(false);
    }
  }, [history, onChange, t, tabs, changed]);

  // value?: number should be value: number, but the @material-ui typing is bugged
  const handleTabChange = (_event: React.ChangeEvent<{}>, value?: number) => {
    if (value !== undefined) {
      onChange(value);
      const tab = tabs[value];
      const title = t(tab.translation);
      window?.history?.replaceState(null, t(title), tab.url);
      setChanged(true);
    }
  };

  return (
    <>
      <Tabs value={value} onChange={handleTabChange} {...restProps} className={classes.root}>
        {tabs.map(({ translation, icon, tooltip }) => {
          const insert = tooltip ? <Tooltip title={tooltip}>{icon}</Tooltip> : icon ? icon : undefined;
          return (
            <Tab
              key={translation}
              label={t(translation)}
              icon={insert}
              className={icon ? classes.iconTab : undefined}
            />
          );
        })}
        {children}
      </Tabs>
    </>
  );
};
